import { AgGridReact } from "ag-grid-react";
import {
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState
} from "react";
import { calculateLogPageStats } from "components/ciq-log-page-header";
import { useHistory, useParams } from "react-router";
import { FilterChipComponentRef } from "entity-app/shared-components/log-render-components/filter-chip-component";
import { AppContext } from "context/AppProvider";
import { applyMaterialLogFilters } from "utils/ag-grid-programmatic-filtering";
import { FeatureTypes } from "entity-app/constants";
import {
  isPermissionNotGrantted,
  ProjectContext
} from "context/ProjectProvider";

import { EmailInviteStatus, ProjectPermissionEnum } from "constants/index";
import { UtilColumnConfig } from "components/column-config-views/column-config-util";
import { TSaveColumnState } from "components/column-config-views";
import { getRowId } from "pages/submittals-log-page/utils";
import { updateGridSelectionOnFilter } from "utils/utils";
import { InfoFeatureType } from "change-events/change-event-polling";
import { useFeatureLogV2InlineEdit } from "entity-app/hooks/feature-log-v2-inline-edit";
import { useLocationBreakdownV2Structure } from "hooks/location-breakdown-v2-structure";
import CiqAgGrid from "components/ciq-ag-grid";
import RequestedDurationDetails from "components/request-durations/requested-duration-details";
import RequestDurationsSingle from "components/request-durations/request-durations-single";
import { Modal } from "antd";
import { backwardCompatibilityForLinkedTasksFilter } from "utils/grid-utils";
import { useMaterialLogData } from "./hooks/material-feature-data";
import { useMaterialLogAgGrid } from "./hooks/material-ag-grid-data";
import MaterialHeaderItems from "./components/header-items";
import { TMaterialLog } from "./model";
import "./material-log.scss";

const featureKey = "materials"; // URL

interface MaterialLogProps {
  pageSidebar: {
    sidebarAction: (params: {
      feature: string;
      action: "open" | "close";
      itemDetailsToSet: any | null;
    }) => void;
    sidebarMetadata: {
      feature: "comments" | string;
      itemDetails: any | null;
    } | null;
    isSidebarOpen: boolean;
  };
}

function MaterialLog({ pageSidebar }: MaterialLogProps) {
  const { sidebarAction, sidebarMetadata, isSidebarOpen } = pageSidebar;
  const { itemDetails } = sidebarMetadata || {};
  const history = useHistory();
  const gridRef = useRef<AgGridReact<any>>(null);
  const {
    projectDetails,
    tokenContents,
    isFeatureFlagEnabled,
    gqlClientForProject,
    loggedInUserProjectProfileResponse
  } = useContext(ProjectContext);

  const [requestingDurationsForFeature, setRequestingDurationsForFeature] =
    useState<any | null>(null);

  const cannotCreateMaterial = isPermissionNotGrantted(
    ProjectPermissionEnum.CreateMaterial,
    tokenContents?.role!
  );

  const canInlineEditMaterialGeneralInfoAndParticipants: any =
    !isPermissionNotGrantted(
      ProjectPermissionEnum.EditMaterialGeneralInfoAndParticipants,
      tokenContents?.role!
    );

  const canInlineEditMaterialCharacteristics: any = !isPermissionNotGrantted(
    ProjectPermissionEnum.EditMaterialCharacteristics,
    tokenContents?.role!
  );

  const canInlineEditMaterialDBOffset: any = !isPermissionNotGrantted(
    ProjectPermissionEnum.EditMaterialDBOffset,
    tokenContents?.role!
  );

  const cannotChangeActualDate = isPermissionNotGrantted(
    ProjectPermissionEnum.changeMaterialActualDate,
    tokenContents?.role!
  );

  const canSetLBSForMaterial = !isPermissionNotGrantted(
    ProjectPermissionEnum.CanSetLBSForMaterial,
    tokenContents?.role!
  );

  const canRequestLeadTime = !isPermissionNotGrantted(
    ProjectPermissionEnum.RequestLeadTime,
    tokenContents?.role!
  );

  const isRequestLeadTimeFeatureEnabled =
    isFeatureFlagEnabled("REQUEST_LEAD_TIME");

  const isMaterialExtractionEnabled = isFeatureFlagEnabled(
    "MATERIAL_EXTRACTION"
  );

  const pageData: any = useMaterialLogData({ gridRef });
  const { projectId } = useParams() as any;

  const {
    isLBSFeatureEnabled,
    LBSOptionsMaster,
    LBSDataInTreeFormat,
    handleOnFilterOpen
  } = useLocationBreakdownV2Structure({
    entityList: pageData.entityList,
    projectId,
    gridRef,
    hasPermissionToSetLBSForMaterial: canSetLBSForMaterial
  });

  const agGridData = useMaterialLogAgGrid({
    featureKey,
    milestonesColumns: pageData.milestonesColumns,
    cannotWriteAccessOfMaterial: cannotCreateMaterial,
    materialHeaderMap: pageData.materialHeaderMap,
    isLBSFeatureEnabled,
    canSetLBSForMaterial
  });

  const { onCellEditRequest } = useFeatureLogV2InlineEdit({
    refetchListData: pageData.fetchLogData,
    infoFeatureType: InfoFeatureType.material
  });
  const filterChipRef = useRef<FilterChipComponentRef>(null);

  const { materialListFilter } = useContext(AppContext);

  const [isFirstDataRendered, setIsFirstDataRendered] = useState(false);
  const [statsData, setStatsData] = useState<{
    displayCount: number;
    filterName: undefined | string;
  }>();
  const [selectedRows, setSelectedRows] = useState<Array<TMaterialLog> | null>(
    null
  );

  const [itemSourceRowIndexForSidepanel, setItemSourceRowIndexForSidepanel] =
    useState<number | null>(null);

  const onFirstDataRendered = useCallback(() => {
    if (materialListFilter) {
      const loggedInUserCompany =
        loggedInUserProjectProfileResponse?.data?.subscription_users[0]?.user
          .project_users[0]?.subscription_vendor?.name;
      applyMaterialLogFilters({
        gridRef,
        materialListFilter,
        additionalData: { loggedInUserCompany }
      });
    }
    if (
      pageData.savedColumns?.material_column_config &&
      materialListFilter === null &&
      gridRef.current
    ) {
      const { columnList } = pageData.savedColumns
        ?.material_column_config as TSaveColumnState;
      const milestones = columnList.find(
        (x: any) => x.field?.name === "_milestones"
      );
      if (milestones) {
        gridRef?.current?.api?.setColumnsVisible(
          pageData.milestonesColumns.dateBlockColumnIds.milestones as any,
          milestones.visibility
        );
      }
      const durations = columnList.find(
        (x: any) => x.field?.name === "_durations"
      );
      if (durations) {
        gridRef?.current?.api?.setColumnsVisible(
          pageData.milestonesColumns.dateBlockColumnIds.durations as any,
          durations.visibility
        );
      }

      const updatedColumnConfig = backwardCompatibilityForLinkedTasksFilter(
        pageData?.savedColumns?.material_column_config?.columnList
      );

      const columnStates = {
        ...pageData?.savedColumns?.material_column_config,
        columnList: updatedColumnConfig
      };

      UtilColumnConfig.setFilterStateFromData({
        columnStates,
        gridRef: {
          api: gridRef.current!.api
        }
      });
    }

    setTimeout(() => {
      // NOTE: To load initial data once in onFilterApplied method
      setIsFirstDataRendered(true);
    }, 500);
  }, [materialListFilter, pageData, loggedInUserProjectProfileResponse]);

  const onEditCell = useCallback(
    (params: any) => {
      gridRef?.current?.api?.setFocusedCell(params.rowIndex, params.colKey);
      gridRef?.current?.api?.startEditingCell({
        rowIndex: params.rowIndex,
        colKey: params.colKey
      });
    },
    [gridRef]
  );

  const onRequestDurationClick = (material: any) => {
    setRequestingDurationsForFeature(material);
  };

  const onLinkClick = (material: any) => {
    history.push(
      `/project/${projectId}/schedule/material-linking?materialId=${material.id}`
    );
  };

  const onCommentClick = ({
    sourceRowIndex
  }: {
    sourceRowIndex: number | undefined | null;
  }) => {
    if (sourceRowIndex === undefined || sourceRowIndex === null) return;
    sidebarAction({
      feature: "comments",
      action: "open",
      itemDetailsToSet: pageData.entityList[sourceRowIndex]
    });
    setItemSourceRowIndexForSidepanel(sourceRowIndex);
  };

  useEffect(() => {
    if (!isSidebarOpen) return;
    if (itemSourceRowIndexForSidepanel !== null) {
      const material = pageData.entityList[itemSourceRowIndexForSidepanel];
      if (!material) return;
      sidebarAction({
        feature: "comments",
        action: "open",
        itemDetailsToSet: material
      });
    }
  }, [
    itemSourceRowIndexForSidepanel,
    pageData.entityList,
    sidebarAction,
    isSidebarOpen
  ]);

  useEffect(() => {
    if (!isSidebarOpen) {
      setItemSourceRowIndexForSidepanel(null);
    }
  }, [isSidebarOpen]);

  const rowClassRules = useMemo(
    () => ({
      "selected-item-for-panel": (params: any) =>
        params.data?.id === itemDetails?.id
    }),
    [itemDetails?.id]
  );

  if (materialListFilter && loggedInUserProjectProfileResponse?.loading) {
    return <div />;
  }

  return (
    <div
      className="flex flex-col h-full w-full"
      data-testid="material-feature-log-page"
    >
      <MaterialHeaderItems
        {...{
          selectedRows,
          gridRef,
          pageData,
          agGridData,
          filterChipRef,
          statsData,
          featureKey,
          projectId,
          pageSidebarState: {
            isSidebarOpen,
            sidebarAction
          }
        }}
      />

      <CiqAgGrid
        {...{
          onFilterOpened: handleOnFilterOpen,
          gridRef,
          columnDefs: agGridData.columnDefs,
          rowData: pageData.entityList,
          defaultColDef: agGridData.defaultColDef,
          rowHeight: 62,
          getRowId, // After refetch the rowdata ,It help to persist Scroll Bar
          readOnlyEdit: true,
          singleClickEdit: true,
          stopEditingWhenCellsLoseFocus: true,
          onCellEditRequest,
          onFirstDataRendered,
          onSortChanged: () => {
            if (isFirstDataRendered && !materialListFilter)
              pageData.onSaveColumnState();
          },
          onFilterChanged: (event: any) => {
            filterChipRef.current?.onFilterApplied();
            setStatsData(
              calculateLogPageStats({
                gridRef,
                ListFilter: materialListFilter,
                featureTypeId: FeatureTypes.MATERIALS
              })
            );

            if (event.source === "quickFilter") return;
            updateGridSelectionOnFilter(event, gridRef);
            if (isFirstDataRendered && !materialListFilter)
              pageData.onSaveColumnState();
          },
          onColumnVisible: (e: any) => {
            if (e.source === "toolPanelUi") {
              // Handle visibility change for milestones and durations columns
              if (!e.column || e?.column?.colId === "_milestones") {
                gridRef?.current?.api?.setColumnsVisible(
                  pageData.milestonesColumns.dateBlockColumnIds
                    .milestones as any,
                  e.visible
                );
              }

              // Handle visibility change for durations column
              if (!e.column || e?.column?.colId === "_durations") {
                gridRef?.current?.api?.setColumnsVisible(
                  pageData.milestonesColumns.dateBlockColumnIds
                    .durations as any,
                  e.visible
                );
              }
              pageData.onSaveColumnState();
            }
          },

          onDragStopped: (e) => {
            const classNames = [
              "ag-header-cell-resize",
              "ag-tool-panel-horizontal-resize"
            ];
            if (e.api && !classNames.includes(e.target.className)) {
              pageData.onSaveColumnState();
            }
          },
          context: {
            projectDetails,
            onEditCell,
            isGridCellEditable: agGridData.isGridCellEditable,
            refetchLogData: pageData.fetchLogData,
            editPermission: !cannotChangeActualDate,
            canInlineEditMaterialGeneralInfoAndParticipants,
            canInlineEditMaterialCharacteristics,
            canInlineEditDBOffset: canInlineEditMaterialDBOffset,
            cannotChangeActualDate,
            canSetLBSForMaterial,
            LBSOptionsMaster,
            LBSDataInTreeFormat,
            gqlClientForProject,
            material_quantity_unit: pageData.allUnits?.material_quantity_unit,
            isRequestLeadTimeFeatureEnabled,
            isMaterialExtractionEnabled,
            canRequestLeadTime,
            onLinkClick,
            onRequestDurationClick,
            materialHeaderMap: pageData.materialHeaderMap,
            history,
            onCommentClick
          },
          onSelectionChanged: () => {
            setSelectedRows(gridRef?.current?.api?.getSelectedRows() || null);
          },
          loading: !isFirstDataRendered,
          onColumnResized: (params: any) => {
            if (params.finished && params.source === "uiColumnResize") {
              // User has finished resizing, do something like save column state
              pageData.onSaveColumnState();
            }
          },
          disableResizingAutoWidth: true,
          rowClassRules
        }}
      />

      <Modal
        title={
          <div className="font-semibold text-sm">
            <div>Request Lead Time</div>
            <div className="text-xs mt-1 font-medium">
              {requestingDurationsForFeature?.name ||
                requestingDurationsForFeature?.title}
            </div>
          </div>
        }
        data-testid="index-modal-request-duration-single"
        open={
          requestingDurationsForFeature &&
          requestingDurationsForFeature?.invite_status !==
            EmailInviteStatus.REQUEST_SENT
        }
        okText="Send Request"
        bodyStyle={{ padding: 0 }}
        footer={null}
        onCancel={() => {
          setRequestingDurationsForFeature(null);
        }}
        destroyOnClose
      >
        <RequestDurationsSingle
          onCancel={() => setRequestingDurationsForFeature(null)}
          onComplete={() => {
            setRequestingDurationsForFeature(null);
          }}
          selectedItem={requestingDurationsForFeature}
        />
      </Modal>

      <Modal
        title={
          <div className="font-semibold text-sm">
            <div>Request Lead Time</div>
            <div className="text-xs mt-1 font-medium">
              {requestingDurationsForFeature?.name ||
                requestingDurationsForFeature?.title}
            </div>
          </div>
        }
        data-testid="index-modal-requested-duration"
        open={
          requestingDurationsForFeature &&
          requestingDurationsForFeature?.invite_status ===
            EmailInviteStatus.REQUEST_SENT
        }
        bodyStyle={{ padding: 0 }}
        footer={null}
        onCancel={() => {
          setRequestingDurationsForFeature(null);
        }}
        destroyOnClose
        className="requested-duration-details-dialog"
      >
        <RequestedDurationDetails
          selectedItem={requestingDurationsForFeature}
          onCancel={() => {
            setRequestingDurationsForFeature(null);
          }}
        />
      </Modal>
    </div>
  );
}

export default MaterialLog;
